import React, { useState } from "react";
import {
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Typography,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  TableContainer,
  Paper,
  TablePagination,
  Chip,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { useEffectAsync } from "../reactHelper";
import { getWithExpiry } from "../common/util/localstorage";
import CollectionActions from "../common/components/CollectionActions";
import TableShimmer from "../common/components/TableShimmer";
import CollectionFab from "../settings/CollectionFab";
import { useDispatch, useSelector } from "react-redux";
import { maintenancesActions } from "../store";
import { useNavigate } from "react-router-dom";
import ErrorDialog from "../common/components/ErrorDialog";

/* -------------------------------- COLORS -------------------------------- */

const statusColors = {
  "en attente": "#FFA500",
  "en cours de reparation": "#1E90FF",
  reparer: "#32CD32",
  retour: "#800080",
  livré: "#008000",
  "non réparable": "#FF0000",
};

/* ------------------------------- COMPONENT ------------------------------- */

const MaintenancesPage = () => {
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [operateurFilter, setOperateurFilter] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [opening, setOpening] = useState(false);
  const [errorMsg, setErrorMsg] = useState("Error !!");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const _maintenances = useSelector((state) => state.maintenances.items);
  const _products = useSelector((state) => state.products.items);
  const _isAdministrator = useSelector(
    (state) => state.session.user?.role
  );

  /* ------------------------------ FETCH DATA ------------------------------ */

  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_URL_NAME}/maintenances`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${getWithExpiry("TOKEN")}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        dispatch(maintenancesActions.setItems(data));
      } else if (response.status === 401) {
        setErrorMsg("UNAUTHORIZED");
        setOpening(true);
        navigate("/login");
      } else {
        const text = await response.text();
        setErrorMsg(text);
        setOpening(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /* ------------------------------ HELPERS ------------------------------ */

  const getProductById = (id) =>
    _products.find((p) => p._id === (id?._id || id));

  const getProductReference = (id) => {
    const p = getProductById(id);
    return p ? `${p.reference} - ${p.designation}` : "N/A";
  };

  const getProductModel = (id) => {
    const p = getProductById(id);
    return p ? `${p.model} - ${p.designation}` : "N/A";
  };

  const getProductMarque = (id) => {
    const p = getProductById(id);
    return p ? `${p.marque} - ${p.designation}` : "N/A";
  };

  const getDuration = (start, end) => {
    const s = start ? new Date(start) : new Date();
    const e = end ? new Date(end) : new Date();
    const days = Math.floor((e - s) / (1000 * 3600 * 24));
    return `${days} j`;
  };

  /* ------------------------------ FILTERING ------------------------------ */

  const filteredMaintenances = _maintenances
    .filter((m) => {
      if (
        m.statut === "livré" &&
        _isAdministrator !== "admin" &&
        _isAdministrator !== "superadmin"
      ) return false;

      if (statusFilter && m.statut !== statusFilter) return false;
      if (operateurFilter && m.operateur !== operateurFilter) return false;

      if (dateDebut && new Date(m.datetime_debut) < new Date(dateDebut))
        return false;
      if (dateFin && new Date(m.datetime_debut) > new Date(dateFin))
        return false;

      return true;
    })
    .filter((m) =>
      m.produit
        ? getProductReference(m.produit)
            .toLowerCase()
            .includes(searchKeyword.toLowerCase())
        : false
    );

  const paginatedMaintenances = filteredMaintenances.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* ------------------------------ PDF EXPORT ------------------------------ */

  const exportPDF = () => {
    const doc = new jsPDF("landscape");

    doc.setFontSize(14);
    doc.text("Rapport des Maintenances", 14, 15);

    doc.setFontSize(10);
    doc.text(
      `Statut: ${statusFilter || "Tous"} | Opérateur: ${
        operateurFilter || "Tous"
      } | Du ${dateDebut || "-"} au ${dateFin || "-"}`,
      14,
      22
    );

    autoTable(doc, {
      startY: 28,
      head: [[
        "#",
        "Opérateur",
        "Produit",
        "Modèle",
        "Marque",
        "Statut",
        "Date début",
        "Date fin",
        "Durée",
        "Remarque",
      ]],
      body: filteredMaintenances.map((m, i) => [
        i + 1,
        m.operateur || "N/A",
        getProductReference(m.produit),
        getProductModel(m.produit),
        getProductMarque(m.produit),
        m.statut,
        m.datetime_debut
          ? new Date(m.datetime_debut).toLocaleString()
          : "-",
        m.datetime_fin
          ? new Date(m.datetime_fin).toLocaleString()
          : "-",
        getDuration(m.datetime_debut, m.datetime_fin),
        m.remarque || "-",
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [0, 35, 85] },
    });

    doc.save("maintenances.pdf");
  };

  /* -------------------------------- RENDER -------------------------------- */

  return (
    <>
      <Typography
        variant="h3"
        align="center"
        sx={{
          mb: 3,
          fontWeight: 800,
          background: "linear-gradient(90deg, #002355, #0081f9)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Maintenances Planifiées
      </Typography>

      {/* ---------------------- MODERN FILTER BAR ---------------------- */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 3, boxShadow: 3 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="center"
        >
          <TextField
            label="Recherche produit"
            size="small"
            fullWidth
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              label="Statut"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">Tous</MenuItem>
              {Object.keys(statusColors)
                .filter(
                  (s) =>
                    _isAdministrator === "superadmin" ||
                    _isAdministrator === "admin" ||
                    s !== "livré"
                )
                .map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Opérateur</InputLabel>
            <Select
              value={operateurFilter}
              label="Opérateur"
              onChange={(e) => setOperateurFilter(e.target.value)}
            >
              <MenuItem value="">Tous</MenuItem>
              {[...new Set(_maintenances.map((m) => m.operateur))]
                .filter(Boolean)
                .map((op) => (
                  <MenuItem key={op} value={op}>
                    {op}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <TextField
            label="Date début"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={dateDebut}
            onChange={(e) => setDateDebut(e.target.value)}
          />

          <TextField
            label="Date fin"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={dateFin}
            onChange={(e) => setDateFin(e.target.value)}
          />

      {(_isAdministrator === "superadmin" || _isAdministrator === "admin") && (
     <Tooltip title="Exporter en PDF">
            <IconButton
              onClick={exportPDF}
              sx={{
                background: "linear-gradient(135deg,#d32f2f,#ff5252)",
                color: "#fff",
                width: 44,
                height: 44,
                "&:hover": {
                  background: "linear-gradient(135deg,#b71c1c,#ff1744)",
                },
              }}
            >
              <PictureAsPdfIcon />
            </IconButton>
          </Tooltip>)}
        </Stack>
      </Paper>

      {/* ---------------------- TABLE (UNCHANGED DESIGN) ---------------------- */}
      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#002355" }}>
              {[
                "#",
                "OPÉRATEUR",
                "PRODUIT",
                "MODELE",
                "MARQUE",
                "STATUT",
                "DATE DÉBUT",
                "DATE FIN",
                "DURÉE",
                "RESSOURCES",
                "REMARQUE",
                "ACTIONS",
              ].map((h) => (
                <TableCell
                  key={h}
                  sx={{ color: "#fff", fontWeight: 700 }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading && paginatedMaintenances.length > 0 ? (
              paginatedMaintenances.map((item, idx) => (
                <TableRow key={item._id} hover>
                  <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                  <TableCell>{item.operateur}</TableCell>
                  <TableCell>{getProductReference(item.produit)}</TableCell>
                  <TableCell>{getProductModel(item.produit)}</TableCell>
                  <TableCell>{getProductMarque(item.produit)}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.statut}
                      size="small"
                      sx={{
                        backgroundColor: statusColors[item.statut],
                        color: "#fff",
                        fontWeight: "bold",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {item.datetime_debut
                      ? new Date(item.datetime_debut).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {item.datetime_fin
                      ? new Date(item.datetime_fin).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {getDuration(
                      item.datetime_debut,
                      item.datetime_fin
                    )}
                  </TableCell>
                  <TableCell>
                    {item.resources?.length
                      ? item.resources.map((r, i) => (
                          <div key={i}>
                            {r.nom} - {r.quantite} {r.unite}
                          </div>
                        ))
                      : "-"}
                  </TableCell>
                  <TableCell>{item.remarque || "-"}</TableCell>
                  <TableCell padding="none">
                    <CollectionActions
                      itemId={item._id}
                      editPath="/settings/maintenance"
                      endpoint="maintenances"
                      action={maintenancesActions}
                      removable
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableShimmer columns={12} endAction />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredMaintenances.length}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(+e.target.value);
          setPage(0);
        }}
      />


      <ErrorDialog
        open={opening}
        errorMsg={errorMsg}
        onResult={() => setOpening(false)}
      />
    </>
  );
};

export default MaintenancesPage;
